import { ReactNode } from 'react';

import Big from 'big.js';
import isNumber from 'lodash/isNumber';
import random from 'lodash/random';
import round from 'lodash/round';

type NumberParser = (val: string) => number;

type ToNumOrFallbackArgs<F> = {
  parser?: NumberParser;
  fallback?: F;
  mapNumber?: (value: number) => number;
};
const toNumOrFallback = <F = undefined>(
  value?: unknown,
  {
    parser = Number,
    fallback = undefined,
    mapNumber,
  }: ToNumOrFallbackArgs<F> = {}
): number | F => {
  if (value instanceof Big) {
    value = value.toNumber();
  }
  if (typeof value === 'number') {
    if (isNaN(value)) {
      return fallback as F;
    }
    return mapNumber ? mapNumber(value) : value;
  }
  if (typeof value === 'string') {
    if (value === '') {
      return fallback as F;
    }
    const parsedNumber = parser(value);
    if (isNaN(parsedNumber)) {
      return fallback as F;
    }
    return mapNumber ? mapNumber(parsedNumber) : parsedNumber;
  }
  return fallback as F;
};

export const toNumOrZero = (
  value?: unknown,
  args: Omit<ToNumOrFallbackArgs<number>, 'fallback'> = {}
): number => {
  return toNumOrFallback(value, { fallback: 0, ...args });
};

export const toBig = (value?: unknown): Big => {
  if (value instanceof Big) {
    return value;
  }
  if (!value) {
    // eslint-disable-next-line no-restricted-syntax
    return Big(0);
  }
  try {
    // eslint-disable-next-line no-restricted-syntax
    return Big(value as string | number);
  } catch {
    // eslint-disable-next-line no-restricted-syntax
    return Big(0);
  }
};

export const nonNegativeGuard = (
  value?: unknown,
  args: Omit<ToNumOrFallbackArgs<number>, 'fallback'> = {}
): number => {
  return Math.max(0, toNumOrZero(value, args));
};

export const toNumOrUndefined = (
  value?: unknown,
  args: Omit<ToNumOrFallbackArgs<number>, 'fallback'> = {}
): number | undefined => {
  return toNumOrFallback(value, { fallback: undefined, ...args });
};

export const toNumOrNull = (
  value?: unknown,
  args: Omit<ToNumOrFallbackArgs<number>, 'fallback'> = {}
): number | null => {
  return toNumOrFallback(value, { fallback: null, ...args });
};

type DashesOrNumArgs = {
  value?: number | string | null;
  roundBy?: number;
  decimals?: number;
  mapNumber?: (val: number) => ReactNode;
  mapDashes?: (dashes: string) => ReactNode;
  parser?: NumberParser;
};
const dashes = (num: number): string => Array(num).fill('-').join('');
export const dashesOrNum = ({
  value: _value,
  roundBy,
  decimals = 2,
  mapNumber,
  mapDashes,
  parser = Number,
}: DashesOrNumArgs): ReactNode => {
  const value = toNumOrUndefined(_value, { parser });

  if (value === undefined) {
    const dashesStr = `${dashes(decimals)}${
      roundBy ? `.${dashes(roundBy)}` : ''
    }`;
    return mapDashes ? mapDashes(dashesStr) : dashesStr;
  }

  const result = roundBy !== undefined ? round(value, roundBy) : value;
  return mapNumber ? mapNumber(result) : result;
};

/**
 * Converts all properties of an object to finite numbers.
 * Non-numeric values (objects, arrays, invalid strings) are treated as zeros.
 */
export const toNumericObject = <T extends Record<string, unknown>>(
  item: T
): Record<keyof T, number> => {
  const result = {} as Record<keyof T, number>;
  for (const key in item) {
    result[key] = item[key] ? toNumOrZero(item[key]) : 0;
  }
  return result;
};

export function sumNumericObjects<
  A extends Record<string, number>,
  B extends Record<string, unknown>
>(a: A, b: B): Record<keyof A, number>;

export function sumNumericObjects<
  A extends Record<string, unknown>,
  B extends Record<string, unknown>
>(a: A, b: B): Record<keyof A, number | null>;

/**
 * Sums numeric properties of two objects key by key.
 * - Non-numeric values (objects, arrays, invalid strings) are treated as null
 * - If one value is numeric and the other is null, the numeric value is used
 * - If both values are null, the result is null (preserves "unknown" state)
 * - Preserves only keys found in object `a`
 */
export function sumNumericObjects<
  A extends Record<string, unknown>,
  B extends Record<string, unknown>
>(a: A, b: B): Record<keyof A, number> | Record<keyof A, number | null> {
  const result = {} as Record<keyof A, number | null>;
  for (const key in a) {
    const v1 = toNumOrNull(a[key]);
    const v2 = toNumOrNull(b[key]);
    if (v1 !== null || v2 !== null) {
      result[key] = (v1 ?? 0) + (v2 ?? 0);
    } else {
      result[key] = null;
    }
  }
  return result;
}

export function divideNumericObject<T extends Record<string, number>>(
  a: T,
  divisor: number
): Record<keyof T, number>;

export function divideNumericObject<T extends Record<string, unknown>>(
  a: T,
  divisor: number
): Record<keyof T, number | null>;

/**
 * Divides numeric properties of an object by the given divisor.
 * Non-numeric values (objects, arrays, invalid strings) are treated as null.
 */
export function divideNumericObject<T extends Record<string, unknown>>(
  a: T,
  divisor: number
): Record<keyof T, number> | Record<keyof T, number | null> {
  const result = {} as Record<keyof T, number | null>;
  for (const key in a) {
    const v = toNumOrNull(a[key]);
    if (v !== null) {
      result[key] = (v ?? 0) / divisor;
    } else {
      result[key] = null;
    }
  }
  return result;
}

export function multiplyNumericObject<T extends Record<string, number>>(
  a: T,
  multiplier: number
): Record<keyof T, number>;

export function multiplyNumericObject<T extends Record<string, unknown>>(
  a: T,
  multiplier: number
): Record<keyof T, number | null>;

/**
 * Multiplies numeric properties of an object by the given multiplier.
 * Non-numeric values (objects, arrays, invalid strings) are treated as null.
 */
export function multiplyNumericObject<T extends Record<string, unknown>>(
  a: T,
  multiplier: number
): Record<keyof T, number> | Record<keyof T, number | null> {
  const result = {} as Record<keyof T, number | null>;
  for (const key in a) {
    const v = toNumOrNull(a[key]);
    if (v !== null) {
      result[key] = (v ?? 0) * multiplier;
    } else {
      result[key] = null;
    }
  }
  return result;
}

export const bigSumBy = <T>(
  arr: T[],
  iteratee: (item: T) => number | Big | undefined
): Big =>
  arr.reduce((acc, item) => acc.plus(toNumOrZero(iteratee(item))), toBig(0));

type RandomArgs = {
  min?: number;
  max?: number;
  fractional?: boolean;
};

type ShiftNumberArgs<T extends string | number> = {
  value: T;
  min?: number;
  max?: number;
  offset?: number;
  percentageOffset?: number;
  keepPrecision?: boolean;
  random?: (options?: RandomArgs) => number;
};
/**
 * @description randomly lowers or increases number by provided offset
 * @param value value to shift
 * @param min min shifted value
 * @param max max shifted value
 * @param offset offset to shift number by
 * @param percentageOffset offset in percents of original value to shift by
 * @param keepPrecision when value is float, result has the same precision
 * @param random custom randomization function
 */
export const shiftNumber = <T extends string | number>({
  value,
  min,
  max,
  offset = 2,
  percentageOffset,
  keepPrecision,
  random: _random = ({ min, max, fractional }: RandomArgs = {}): number =>
    random(min ?? 0, max ?? Number.MAX_SAFE_INTEGER, !!fractional),
}: ShiftNumberArgs<T>): T => {
  const [, fractionals] = String(value).split('.');
  let fractional = false;
  let precision = 0;
  if (fractionals) {
    fractional = true;
    precision = fractionals.length;
  }
  const number = toBig(value).toNumber();
  if (percentageOffset !== undefined) {
    offset = (number / 100) * percentageOffset;
  }
  const newNumber = _random({
    min: min !== undefined ? Math.max(number - offset, min) : number - offset,
    max: max ? Math.min(number + offset, max) : number + offset,
    fractional,
  });
  const result =
    precision && keepPrecision ? round(newNumber, precision) : newNumber;
  if (typeof value === 'number') {
    return result as T;
  }
  return String(result) as T;
};

export const compactNumber = (
  value?: number | string | null,
  options?: Intl.NumberFormatOptions
): string => {
  const numericVal = toNumOrUndefined(value);
  if (numericVal === undefined) {
    return '';
  }
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
    ...options,
  }).format(numericVal);
};

export const toNumberWithSeparators = (
  value?: number | string | null
): string => {
  const numericVal = toNumOrUndefined(value);
  if (numericVal === undefined) {
    return '';
  }
  return numericVal.toLocaleString('en-US');
};

/**
 * Checks if value is a number and is positive
 */
export const isPositiveNumber = (
  value?: number | string | null
): value is number => {
  return isNumber(value) && value > 0;
};

/** Ordinal suffix for a number (1st, 2nd, 3rd, 11th–13th, 21st, …). */
export const getOrdinalSuffix = (n: number): string => {
  const rem = Math.abs(n) % 100;
  if (rem >= 11 && rem <= 13) {
    return 'th';
  }
  switch (rem % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

/** Formats a number with its ordinal suffix (e.g. 1 → "1st"). */
export const toOrdinal = (n: number): string => `${n}${getOrdinalSuffix(n)}`;
